import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { UserRole } from '../lib/roleUtils';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  roles: UserRole[];
  activeRole: UserRole | null;
  displayName: string;
  setActiveRole: (role: UserRole) => Promise<void>;
  signUp: (email: string, password: string, fullName: string, signupRole: 'player' | 'owner', courseData?: { name: string; holes: number; address?: string; city: string; state: string; country: string }, playerState?: string) => Promise<{ error: AuthError | null }>;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [activeRole, setActiveRoleState] = useState<UserRole | null>(null);
  const [displayName, setDisplayName] = useState<string>('User');

  const loadUserSession = async (userId: string | null, userObj: User | null) => {
    if (!userId) {
      setRoles([]);
      setActiveRoleState(null);
      setDisplayName('User');
      return;
    }

    try {
      const rolesResponse = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId);

      const profileResponse = await supabase
        .from('profiles')
        .select('active_role, first_name, last_name')
        .eq('id', userId)
        .single();

      const userRoles = (rolesResponse.data ?? []).map((r: any) => r.role as UserRole);
      const userActiveRole = (profileResponse.data as any)?.active_role ?? null;
      
      const profileFirstName = (profileResponse.data as any)?.first_name?.trim() || '';
      const profileLastName = (profileResponse.data as any)?.last_name?.trim() || '';
      const metadataFirstName = userObj?.user_metadata?.first_name?.trim() || '';
      const metadataLastName = userObj?.user_metadata?.last_name?.trim() || '';
      
      const firstName = profileFirstName || metadataFirstName;
      const lastName = profileLastName || metadataLastName;
      const fullName = `${firstName} ${lastName}`.trim();

      setRoles(userRoles);
      setActiveRoleState(userActiveRole as UserRole | null);
      setDisplayName(fullName || 'User');
    } catch (error) {
      console.error('Error loading user session:', error);
      setRoles([]);
      setActiveRoleState(null);
      
      const metadataFirstName = userObj?.user_metadata?.first_name?.trim() || '';
      const metadataLastName = userObj?.user_metadata?.last_name?.trim() || '';
      const fallbackName = `${metadataFirstName} ${metadataLastName}`.trim();
      setDisplayName(fallbackName || 'User');
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      loadUserSession(session?.user?.id ?? null, session?.user ?? null).finally(() => setLoading(false));
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      loadUserSession(session?.user?.id ?? null, session?.user ?? null).finally(() => setLoading(false));
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, fullName: string, signupRole: 'player' | 'owner', courseData?: { name: string; holes: number; address?: string; city: string; state: string; country: string }, playerState?: string) => {
    const nameParts = fullName.trim().split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    const metadata: Record<string, string | number> = {
      first_name: firstName,
      last_name: lastName,
      signup_role: signupRole,
    };

    if (signupRole === 'owner' && courseData) {
      metadata.course_name = courseData.name;
      metadata.course_holes = courseData.holes;
      if (courseData.address) metadata.course_address = courseData.address;
      metadata.course_city = courseData.city;
      metadata.course_state = courseData.state;
      metadata.course_country = courseData.country;
    }

    const waitlistRedirect =
      signupRole === 'player' &&
      import.meta.env.VITE_WAITLIST_MODE === 'true' &&
      playerState;

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: waitlistRedirect
          ? `${window.location.origin}/login?waitlisted=1&state=${encodeURIComponent(playerState!)}`
          : `${window.location.origin}/login`,
        data: metadata,
      },
    });

    if (!error && data.user) {
      try {
        const profileData: Record<string, unknown> = {
          id: data.user.id,
          first_name: firstName,
          last_name: lastName,
        };
        if (signupRole === 'player' && import.meta.env.VITE_WAITLIST_MODE === 'true' && playerState) {
          profileData.state = playerState;
          profileData.waitlist_status = 'waitlisted';
        }
        const { error: profileError } = await supabase
          .from('profiles')
          .upsert(profileData, {
            onConflict: 'id'
          });

        if (profileError) {
          console.error('Error creating user profile:', profileError);
        }

        const { error: roleError } = await supabase
          .from('user_roles')
          .insert({ user_id: data.user.id, role: signupRole });

        if (roleError) {
          console.error('Error creating user role:', roleError);
        }
      } catch (profileError) {
        console.error('Failed to create user profile:', profileError);
      }
    }

    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut({ scope: 'global' });
    } catch {
      // ignore errors (e.g. already-expired token)
    }
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('sb-')) localStorage.removeItem(key);
    });
    window.location.href = '/login';
  };

  const setActiveRole = async (role: UserRole) => {
    if (!user?.id) return;

    if (!roles.includes(role)) {
      console.error(`Cannot set active role to ${role}: user does not possess this role`);
      return;
    }

    const { error } = await supabase
      .from('profiles')
      .update({ active_role: role } as any)
      .eq('id', user.id);

    if (error) {
      console.error('Error updating active role:', error);
      return;
    }

    setActiveRoleState(role);
  };

  const value = {
    user,
    session,
    loading,
    roles,
    activeRole,
    displayName,
    setActiveRole,
    signUp,
    signIn,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}